// Misc
import React, { PureComponent, Fragment } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import pick from 'lodash/pick'
import keys from 'lodash/keys'
import some from 'lodash/some'
import size from 'lodash/size'
import { withStyles } from '@material-ui/core/styles'

// Utils
import { momentOffset } from 'utils/time'

// Validation
import { Validator } from 'react-vld'
import { validateRequiredDate, validateMinDate, validateMaxDate } from 'utils/validation'

// Atoms
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import Input, { InputPropTypes } from 'atoms/Input'
import Calendar from 'atoms/Calendar'
import Popover from 'atoms/Popover'

// Icons
import CloseIcon from '@material-ui/icons/Close'

// Styles
const styles = theme => ({
  root: {
    display: 'flex'
  },
  rootWithErrorMessage: {
    '& $buttonLabel': {
      color: theme.palette.error.main
    }
  },
  rootDisabled: {
    '& $button': {
      cursor: 'default',
      opacity: 0.5,
    }
  },
  button: {
    border: 0,
    padding: 0,
    margin: 0,
    outline: 0,
    background: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    transition: theme.transitions.create(['opacity']),
  },
  buttonLabel: {
    height: '2rem',
    lineHeight: 'normal',
    display: 'flex',
    alignItems: 'center',
  },
  deleteButton: {
    width: '2rem',
    height: '2rem',
  },
  deleteButtonIcon: {
    width: theme.spacing.unit * 2,
    height: theme.spacing.unit * 2,
  },
  calendarTitle: {
    display: 'flex',
    alignItems: 'center',
  },
  calendarTitleLabel: {
    flex: 1,
    textAlign: 'center',
  },
  calendarWeekDayName: {
    textAlign: 'center',
  },
  calendarDay: {
    width: '2rem',
    height: '2rem',
    display: 'flex',
    borderRadius: '50%',
    alignItems: 'center',
    justifyContent: 'center',
    margin: theme.spacing.unit / 2,
    transition: theme.transitions.create(['background-color', 'color', 'opacity']),
  },
  calendarDayDisabled: {
    opacity: 0.5,
  },
  calendarDaySelected: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  calendarDayHighlightWeek: {
    borderRadius: 0,
    margin: 0,
  },
  calendarDaySelectedWeekStart: {
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
  },
  calendarDaySelectedWeekEnd: {
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
  }
})

const dateFormat = 'ddd, MMM Do'

@withStyles(styles)
class DatePicker extends PureComponent {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    validation: PropTypes.object,
    date: PropTypes.instanceOf(Date),
    defaultDate: PropTypes.instanceOf(Date),
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date),
    utcOffset: PropTypes.number,
    disabled: PropTypes.bool,
    highlightWeek: PropTypes.bool,
    availability: PropTypes.arrayOf(PropTypes.shape({
      weekDay: PropTypes.number.isRequired,
    })),
  }

  static defaultProps = {
    onChange: () => {},
    placeholder: 'Enter date',
  }

  constructor({ date, defaultDate }) {
    super()
    this.state = {
      date: date || defaultDate,
      anchorEl: null
    }
  }

  static getDerivedStateFromProps = (nextProps, prevState) => {
    const { utcOffset, date } = nextProps
    if (
      date !== undefined
      && !momentOffset(utcOffset, date).isSame(prevState.date)
    ) {
      return { date }
    }
    return null
  }

  isDayDisabled = (date) => {
    const { availability, utcOffset } = this.props
    if (!size(availability)) return false
    const dateMoment = momentOffset(utcOffset, date)
    return !some(availability, ({ weekDay }) => dateMoment.isoWeekday() === weekDay)
  }

  invokeChange = (date) => {
    const { onChange, validation } = this.props
    if (validation) validation.reset({ updateComponent: false })
    this.setState({ date, anchorEl: null }, () => onChange(date))
  }

  handleButtonClick = (ev) => {
    const { disabled } = this.props
    if (!disabled) {
      this.setState({ anchorEl: ev.currentTarget })
    }
  }

  handlePopoverClose = () => {
    this.setState({ anchorEl: null })
  }

  handleCalendarSelect = (date) => {
    this.invokeChange(date)
  }

  handleDelete = () => {
    this.invokeChange(null)
  }

  renderCalendarTitle = ({ label, prevButton, nextButton }) => {
    const { classes } = this.props
    return (
      <div className={classes.calendarTitle}>
        { prevButton }
        <Typography className={classes.calendarTitleLabel}>
          { label }
        </Typography>
        { nextButton }
      </div>
    )
  }

  renderCalendarWeekDayName = ({ label }) => {
    const { classes } = this.props
    return (
      <Typography variant="caption" color="textSecondary" className={classes.calendarWeekDayName}>
        { label }
      </Typography>
    )
  }

  renderCalendarDay = ({ date, isSelected, isDisabled }) => {
    const { classes, utcOffset, highlightWeek } = this.props
    const dateMoment = momentOffset(utcOffset, date)

    let weekSelected = false
    if (highlightWeek) {
      const { date: selectedDate } = this.state
      weekSelected = dateMoment.isSame(selectedDate, 'isoWeek')
    }

    const cx = classNames(classes.calendarDay, {
      [classes.calendarDaySelected]: isSelected || weekSelected,
      [classes.calendarDayDisabled]: isDisabled && !weekSelected,
      [classes.calendarDayHighlightWeek]: highlightWeek,
    })

    return (
      <Typography className={cx}>
        { dateMoment.format('D') }
      </Typography>
    )
  }

  renderLabel() {
    const { utcOffset, highlightWeek } = this.props
    const { date } = this.state
    const dateMoment = momentOffset(utcOffset, date)

    if (highlightWeek) {
      const weekStart = dateMoment.clone().startOf('isoWeek')
      const weekEnd = dateMoment.clone().endOf('isoWeek')
      return `${weekStart.format(dateFormat)} - ${weekEnd.format(dateFormat)}`
    }

    return dateMoment.format(dateFormat)
  }

  render() {
    const {
      classes, placeholder, required, validation, minDate, maxDate, utcOffset, disabled
    } = this.props
    const { anchorEl, date } = this.state
    const InputProps = pick(this.props, keys(InputPropTypes))
    const errorMessage = validation && validation.isInvalid ? validation.error.message : null
    const cx = classNames(classes.root, {
      [classes.rootWithErrorMessage]: !!errorMessage,
      [classes.rootDisabled]: disabled,
    })
    return (
      <Fragment>
        <Popover
          open={!!anchorEl}
          classes={{ paper: 'asd' }}
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          onClose={this.handlePopoverClose}
        >
          <Calendar
            renderTitle={this.renderCalendarTitle}
            renderWeekDayName={this.renderCalendarWeekDayName}
            renderDay={this.renderCalendarDay}
            selectedDate={date}
            minDate={minDate}
            maxDate={maxDate}
            utcOffset={utcOffset}
            onSelect={this.handleCalendarSelect}
            isDayDisabled={this.isDayDisabled}
          />
        </Popover>
        <Input {...InputProps} errorMessage={errorMessage}>
          { ({ id }) => (
            <div className={cx}>
              <button
                id={id}
                type="button"
                tabIndex="-1"
                className={classes.button}
                onClick={this.handleButtonClick}
              >
                <Typography
                  className={classes.buttonLabel}
                  color={date ? 'default' : 'textSecondary'}
                >
                  { date
                    ? this.renderLabel()
                    : placeholder
                  }
                </Typography>
              </button>
              { !required && !!date && (
                <IconButton className={classes.deleteButton} onClick={this.handleDelete}>
                  <CloseIcon className={classes.deleteButtonIcon} />
                </IconButton>
              ) }
            </div>
          ) }
        </Input>
      </Fragment>
    )
  }
}

export default class DatePickerValidator extends PureComponent { // eslint-disable-line
  static propTypes = {
    validation: PropTypes.object,
    required: PropTypes.bool,
    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date),
    minDateErrorMessage: PropTypes.any,
    maxDateErrorMessage: PropTypes.any,
    innerRef: PropTypes.func,
  }

  static defaultProps = {
    innerRef: () => {},
  }

  validate = () => {
    const { required, minDate, maxDate, minDateErrorMessage, maxDateErrorMessage } = this.props
    const { date } = this.datePicker.state
    if (required) validateRequiredDate(date, 'Required field')
    if (minDate) validateMinDate(date, minDate, minDateErrorMessage)
    if (maxDate) validateMaxDate(date, maxDate, maxDateErrorMessage)
  }

  innerRef = (component) => {
    const { innerRef } = this.props
    this.datePicker = component
    innerRef(component)
  }

  render() {
    const {
      minDate, minDateErrorMessage, maxDate, maxDateErrorMessage,
      validation, required, innerRef, ...rest
    } = this.props
    return (
      <Validator rule={this.validate}>
        { innerValidation => (
          <DatePicker
            {...rest}
            validation={validation || innerValidation}
            innerRef={this.innerRef}
            required={required}
            minDate={minDate}
            maxDate={maxDate}
          />
        ) }
      </Validator>
    )
  }
}
